import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  META_BUDGET_SCHEDULE_INTERVAL_SEC,
  snapUnixSecondsToMetaBudgetScheduleUtc,
} from "@common/utils/campaignBudgetMeta";
import {
  resolveEventWallTimezone,
  scheduleInputToEventWallMinute,
} from "./eventWallMinuteStrings";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Minimum gap between phase start and end (matches Meta schedule quantum). */
export const META_PHASE_MIN_GAP_MINUTES =
  META_BUDGET_SCHEDULE_INTERVAL_SEC / 60;

/**
 * Snap an event-wall minute to Meta’s **UTC** 15-minute grid, expressed back as event-wall
 * `YYYY-MM-DDTHH:mm` (same convention as the rest of guided edit).
 *
 * - **`start`**: floor in UTC (phase start).
 * - **`end`**: ceil in UTC (phase end), matches Meta’s high-demand window end snapping.
 */
export function snapWallMinuteToMetaBudgetUtc(
  wallInput: string | undefined,
  eventTimezone: string,
  bound: "start" | "end"
): string {
  const wall = wallInput?.trim()
    ? scheduleInputToEventWallMinute(wallInput, eventTimezone)
    : "";
  if (!wall) return "";
  const tz = resolveEventWallTimezone(eventTimezone);
  const d = dayjs.tz(wall.slice(0, 16), tz);
  if (!d.isValid()) return "";
  const unixSeconds = Math.floor(d.valueOf() / 1000);
  const snapped = snapUnixSecondsToMetaBudgetScheduleUtc(
    unixSeconds,
    bound === "start" ? "floor" : "ceil"
  );
  return dayjs.unix(snapped).tz(tz).format("YYYY-MM-DDTHH:mm");
}

/**
 * Picker `onChange` value → event wall minute, snapped for Meta `budget_schedules`.
 * Uses the absolute instant (`valueOf`) then formats in the event zone so it stays correct even
 * when the picker’s `Dayjs` instance is not tz-plugin extended the same way as this module.
 */
/**
 * Picker `onChange` value → event wall `YYYY-MM-DDTHH:mm` in `eventTimezone` (minute precision,
 * no Meta UTC quarter-hour snap). Use for campaign flight pickers; keep
 * {@link snapPickerDayjsToWallMinute} for phased budget segments that must sit on Meta’s grid.
 */
export function formatPickerDayjsToEventWallMinute(
  value: Dayjs | null,
  eventTimezone: string
): string {
  if (!value?.isValid()) return "";
  const tz = resolveEventWallTimezone(eventTimezone);
  return dayjs(value.valueOf()).tz(tz).format("YYYY-MM-DDTHH:mm");
}

export function snapPickerDayjsToWallMinute(
  value: Dayjs | null,
  eventTimezone: string,
  bound: "start" | "end"
): string {
  if (!value?.isValid()) return "";
  const tz = resolveEventWallTimezone(eventTimezone);
  const wall = dayjs(value.valueOf())
    .tz(tz)
    .format("YYYY-MM-DDTHH:mm");
  return snapWallMinuteToMetaBudgetUtc(wall, tz, bound);
}

/**
 * Add wall-clock minutes in `eventTimezone`, then snap for Meta (`start` = floor UTC,
 * `end` = ceil UTC).
 */
export function addWallMinutesThenSnapWallMinute(
  wall: string,
  eventTimezone: string,
  minutes: number,
  snapBound: "start" | "end"
): string {
  const tz = resolveEventWallTimezone(eventTimezone);
  const base = scheduleInputToEventWallMinute(wall, eventTimezone);
  if (!base) return "";
  const d = dayjs.tz(base.slice(0, 16), tz);
  if (!d.isValid()) return "";
  const moved = d.add(minutes, "minute");
  const raw = moved.tz(tz).format("YYYY-MM-DDTHH:mm");
  return snapWallMinuteToMetaBudgetUtc(raw, tz, snapBound);
}
